import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  Typography,
  Autocomplete,
  TextField,
  Checkbox,
  Stack,
} from "@mui/material";

import api from "../../api";
import { insightsSectionClassName } from "../../common/utils/insightsDesignEnabled";
import QRScans from "./QRScans";
import PageViews from "./PageViews";
import BarcodeList from "./Barcode/List";
import BarcodeDiagnosticsReport from "./Barcode/Diagnostics";
import DiagnosticsTable from "./Product/DiagnosticsTable";
import NoBarcodeProducts from "./Product/NoBarcodeProducts";
import BarcodeSourceList from "./Barcode/SourceList";
import BarcodeProductList from "./Barcode/ProductsList";
import OrphanProductsTable from "./Product/OrphanProductsTable";
import ProductsMostWidelyUsed from "./Product/ProductsMostWidelyUsed";
import MultipleBarcodesProducts from "./Product/MultipleBarcodesProducts";
import PriceReportHost from "./PriceReports/PriceReportHost";
import BarcodeMissingReport from "./Missing/BarcodeMissingReport";
import ProductOverridesMissingReport from "./Missing/ProductOverridesMissingReport";
import PriceHistoryOmittedReport from "./PriceHistoryOmitted/PriceHistoryOmittedReport";
import LocationMissingReport from "./LocationMissing/LocationMissingReport";
import LocationLabelReport from "./LocationLabel/LocationLabelReport";
import ProductFrequencyReport from "./ProductFrequency/ProductFrequencyReport";
import StreetTeamReport from "./StreetTeam/StreetTeamReport";

const DEFAULT_REPORT_KEY = "barcodes-source";

const ReportsWithFilter = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [checked, setChecked] = useState(false);
  const user = useSelector((state) => state.user);
  const isAdminUser = api.isAdmin(user);

  const options = [
    {
      id: 8,
      key: "qr-scans",
      label: "QR Scans",
      component: <QRScans showFullData={checked} />,
    },
    {
      id: 9,
      key: "page-views",
      label: "Page Views",
      component: <PageViews showFullData={checked} />,
    },
    // {
    //   id: 10,
    //   label: "Visits By Page",
    //   component: <VisitByPage showFullData={checked} />,
    // },
    {
      id: 1,
      key: "products-most-widely-used",
      label: "Products / Most widely used",
      component: <ProductsMostWidelyUsed />,
      hide: !isAdminUser,
    },
    {
      id: 2,
      key: "products-multiple-barcodes",
      label: "Products / Multiple barcodes",
      component: <MultipleBarcodesProducts showFullData={checked} />,
    },
    {
      id: 3,
      key: "products-no-barcode",
      label: "Products / No barcode",
      component: <NoBarcodeProducts showFullData={checked} />,
    },
    {
      id: 4,
      key: "price-retailer",
      label: "Price / Retailer",
      component: <PriceReportHost report="retailer" />,
      hideFromDropdown: true,
    },
    {
      id: 15,
      key: "price-product",
      label: "Price / Product",
      component: <PriceReportHost report="product" />,
      hideFromDropdown: true,
    },
    {
      id: 16,
      key: "price-province",
      label: "Price / Province",
      component: <PriceReportHost report="province" />,
      hideFromDropdown: true,
    },
    {
      id: 5,
      key: "product-weight",
      label: "Product / Weight ",
      component: <h1>Products / Weight</h1>,
    },
    {
      id: 6,
      key: "product-orphan",
      label: "Product / Orphan",
      component: <OrphanProductsTable />,
    },
    {
      id: 7,
      key: "product-diagnostics",
      label: "Product / Diagnostics",
      component: <DiagnosticsTable />,
    },
    {
      id: 12,
      key: "barcodes-list",
      label: "Barcodes / List",
      component: <BarcodeList showFullData={checked} />,
    },
    {
      id: 13,
      key: "barcodes-product",
      label: "Barcodes / Product",
      component: <BarcodeProductList showFullData={checked} />,
    },
    {
      id: 14,
      key: "barcodes-source",
      label: "Barcodes / Source",
      component: <BarcodeSourceList showFullData={checked} />,
    },
    {
      id: 11,
      key: "barcodes-diagnostics",
      label: "Barcodes / Diagnostics",
      component: <BarcodeDiagnosticsReport showFullData={checked} />,
    },
    {
      id: 17,
      key: "barcode-missing",
      label: "Barcode / Missing",
      component: <BarcodeMissingReport />,
    },
    {
      id: 18,
      key: "product-overrides-missing",
      label: "Product Overrides / Missing",
      component: <ProductOverridesMissingReport />,
    },
    {
      id: 19,
      key: "price-history-omitted",
      label: "Price History / Omitted",
      component: <PriceHistoryOmittedReport />,
    },
    {
      id: 20,
      key: "location-missing",
      label: "Location / Missing",
      component: <LocationMissingReport />,
    },
    {
      id: 21,
      key: "location-label",
      label: "Location / Label",
      component: <LocationLabelReport />,
    },
    {
      id: 22,
      key: "product-frequency",
      label: "Product / Frequency",
      component: <ProductFrequencyReport />,
    },
    {
      id: 23,
      key: "street-team-leaderboard",
      label: "Street team / Leaderboard",
      component: <StreetTeamReport variant="leaderboard" />,
    },
    {
      id: 24,
      key: "street-team-retailer",
      label: "Street team / Retailer",
      component: <StreetTeamReport variant="retailer" />,
    },
    {
      id: 25,
      key: "street-team-beef-cuts",
      label: "Street team / Beef cuts",
      component: <StreetTeamReport variant="cuts" />,
    },
  ];
  const availableOptions = options.filter(({ hide }) => !hide);
  const dropdownOptions = availableOptions.filter(({ hideFromDropdown }) => !hideFromDropdown);
  const selectedReportKey = searchParams.get("report");
  const defaultOption =
    dropdownOptions.find(({ key }) => key === DEFAULT_REPORT_KEY) ||
    dropdownOptions[0];
  const selectedOption =
    availableOptions.find(({ key }) => key === selectedReportKey) ||
    defaultOption;
  const ReportComponent = selectedOption?.component;

  useEffect(() => {
    if (!selectedOption) {
      return;
    }

    if (selectedReportKey !== selectedOption.key) {
      const nextSearchParams = new URLSearchParams(searchParams);
      nextSearchParams.set("report", selectedOption.key);
      setSearchParams(nextSearchParams, { replace: true });
    }
  }, [searchParams, selectedOption, selectedReportKey, setSearchParams]);

  return (
    <Box sx={{ pt: 0 }}>
      <Box className={insightsSectionClassName("insights-section--filter")}>
        <Typography className="form-section-title" variant="h4" component="h4" sx={{ mb: 1 }}>
          Reports
        </Typography>

      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems={{ md: "center" }}
        sx={{ width: "100%", mb: 2 }}
      >
        <Autocomplete
          id="report-select"
          sx={{ width: "100%", maxWidth: 600 }}
          options={dropdownOptions.map((option) => {
            const { component, ...rest } = option;
            return rest;
          })}
          value={selectedOption}
          isOptionEqualToValue={(option, value) => option?.id === value?.id}
          onChange={(_, option) => {
            const nextOptionKey = option?.key || defaultOption?.key;

            if (!nextOptionKey) {
              return;
            }

            const nextSearchParams = new URLSearchParams(searchParams);
            nextSearchParams.set("report", nextOptionKey);
            setSearchParams(nextSearchParams);
          }}
          autoHighlight
          getOptionLabel={(option) => option.label}
          renderOption={(props, option) => (
            <Box
              component="li"
              sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
              value={option?.id}
              {...props}
            >
              {option.label}
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select a report"
              inputProps={{
                ...params.inputProps,
                autoComplete: "off", // disable autocomplete and autofill
              }}
            />
          )}
        />
        {isAdminUser && (
          <Box
            component="label"
            sx={{ display: "flex", alignItems: "center", cursor: "pointer", flexShrink: 0 }}
          >
            <Checkbox
              checked={checked}
              onChange={() => {
                setChecked(!checked);
              }}
            />
            Show reports on all data
          </Box>
        )}
      </Stack>
      </Box>
      {ReportComponent}
    </Box>
  );
};

export default ReportsWithFilter;
